import apiClient from './client';
import { ApiResponse } from '@/types';

/**
 * AI developer and reviewer sessions, project profiles and releases.
 */

export type AgentState = 'queued' | 'running' | 'idle' | 'waiting_human' | 'paused' | 'done' | 'failed' | 'cancelled';

export interface AgentSession {
  sessionId: string;
  role: 'developer' | 'reviewer' | 'qa';
  aiModel: string;
  effort?: string;
  agentProfile: string;
  billing: 'api' | 'subscription';
  turnBudget: number;
  resumeAfter?: string;
  handover?: { toProfile: string; by?: string; at: string };
  gitlabProjectId: number;
  projectPath?: string;
  iid?: number;
  mrIid?: number;
  title?: string;
  branch?: string;
  baseBranch?: string;
  worktree?: string;
  tmux?: string;
  state: AgentState;
  lock?: { holder?: 'runner' | 'human'; by?: string };
  stopRequested?: string;
  inbox?: Array<{ mid: string; from: string; source: string; text?: string; at: string; deliveredAt?: string }>;
  step?: string;
  turns: number;
  costUsd: number;
  budgetUsd: number;
  heartbeatAt?: string;
  lastResult?: { subtype: string; text?: string; at: string };
  error?: string;
  requestedBy?: { kind: string; username?: string };
  createdAt: string;
  updatedAt: string;
}

export interface GatewayOutcome {
  actionId: string;
  decision: string;
  mode: string;
  denyReason?: string;
}

export interface AgentProfiles {
  profiles: Array<{ name: string; billing: 'api' | 'subscription' }>;
  default: string;
  enabled: boolean;
}

export async function getProfiles(): Promise<AgentProfiles> {
  const response = await apiClient.get<ApiResponse<AgentProfiles>>('/agents/profiles');
  return response.data.data as AgentProfiles;
}

export interface LogLine {
  seq: number;
  at: string;
  text: string;
}

export async function getAgentLog(id: string, after: number): Promise<LogLine[]> {
  const response = await apiClient.get<ApiResponse<LogLine[]>>(`/agents/${id}/log`, { params: { after } });
  return response.data.data || [];
}

export async function listAgents(active: boolean): Promise<AgentSession[]> {
  const response = await apiClient.get<ApiResponse<AgentSession[]>>('/agents', { params: { active } });
  return response.data.data || [];
}

export async function getAgent(id: string): Promise<AgentSession> {
  const response = await apiClient.get<ApiResponse<AgentSession>>(`/agents/${id}`);
  return response.data.data as AgentSession;
}

export async function messageAgent(id: string, text: string): Promise<void> {
  await apiClient.post(`/agents/${id}/message`, { text });
}

export async function controlAgent(id: string, action: 'pause' | 'resume' | 'cancel', body: Record<string, unknown> = {}): Promise<void> {
  await apiClient.post(`/agents/${id}/${action}`, body);
}

export async function dispatchAgent(input: {
  project: string;
  iid?: number;
  mrIid?: number;
  role: 'developer' | 'reviewer' | 'qa';
  profile?: string;
  instructions?: string;
}): Promise<GatewayOutcome> {
  const response = await apiClient.post<ApiResponse<GatewayOutcome>>('/agents/dispatch', input);
  return response.data.data as GatewayOutcome;
}

export interface ProjectProfile {
  gitlabProjectId: number;
  projectPath: string;
  baseBranch: string;
  companionProjectPath?: string;
  ciJobs: { unitTests: string; deployReview: string; stopReview: string; deployMain: string };
  reviewApps: boolean;
  reviewAppUrl?: string;
  mainEnvUrl?: string;
  qa?: boolean;
  aiReview?: boolean; // a code review before QA
  live?: boolean; // the manager acts on it; otherwise it only records what it would do
  liveSource?: 'settings' | 'app'; // the deploy settings, or the CEO's "Make live"
  packages: Array<{ name: string; versionFile: string; dependents: Array<{ projectPath: string; dependencyName?: string }> }>;
  agentProfile?: string;
  notes?: string;
  stored: boolean;
  discoveredAt?: string;
  confirmedAt?: string;
  confirmedBy?: string;
}

export async function listProfiles(): Promise<ProjectProfile[]> {
  const response = await apiClient.get<ApiResponse<ProjectProfile[]>>('/project-profiles');
  return response.data.data || [];
}

export async function discoverProfile(project: string): Promise<ProjectProfile> {
  const response = await apiClient.post<ApiResponse<ProjectProfile>>(`/project-profiles/${encodeURIComponent(project)}/discover`);
  return response.data.data as ProjectProfile;
}

/** Is the project ready for AI management? The check also adds the missing bot users (projects/setup-check.ts) */
export interface SetupCheck {
  key: string;
  title: string;
  state: 'ok' | 'fixed' | 'warn' | 'missing';
  detail: string;
  how?: string;
}

export interface SetupReport {
  gitlabProjectId: number;
  projectPath: string;
  ready: boolean;
  checks: SetupCheck[];
  checkedAt: string;
}

export async function checkProjectSetup(project: string): Promise<SetupReport> {
  const response = await apiClient.post<ApiResponse<SetupReport>>(`/project-profiles/${encodeURIComponent(project)}/check`);
  return response.data.data as SetupReport;
}

/** Add a GitLab project to the app by path or link (CEO); it starts in shadow */
export async function importProject(project: string): Promise<ProjectProfile & { created: boolean }> {
  const response = await apiClient.post<ApiResponse<ProjectProfile & { created: boolean }>>('/project-profiles/import', { project });
  return response.data.data as ProjectProfile & { created: boolean };
}

export async function setProjectLive(project: string, live: boolean): Promise<ProjectProfile> {
  const response = await apiClient.put<ApiResponse<ProjectProfile>>(`/project-profiles/${encodeURIComponent(project)}/live`, { live });
  return response.data.data as ProjectProfile;
}

export async function saveProfile(project: string, profile: Partial<ProjectProfile>): Promise<ProjectProfile> {
  const response = await apiClient.put<ApiResponse<ProjectProfile>>(`/project-profiles/${encodeURIComponent(project)}`, profile);
  return response.data.data as ProjectProfile;
}

export interface ReleaseRecord {
  _id: string;
  projectPath: string;
  package?: string;
  tag: string;
  url?: string;
  releasedAt: string;
  issues: number[];
  announcedAt?: string;
  bumpMrs: Array<{ projectPath: string; mrIid: number; url?: string; state?: string }>;
}

export async function listReleases(): Promise<ReleaseRecord[]> {
  const response = await apiClient.get<ApiResponse<ReleaseRecord[]>>('/project-profiles/releases/list');
  return response.data.data || [];
}

export async function releaseStatus(pkg: string): Promise<string> {
  const response = await apiClient.get<ApiResponse<{ text: string }>>(`/project-profiles/releases/status/${encodeURIComponent(pkg)}`);
  return response.data.data?.text || '';
}
